import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Zap, CheckCircle, XCircle, RefreshCw, Copy, Terminal,
  MessageSquare, BarChart3, Users, Calendar, Wifi, WifiOff,
  Play, BookOpen, Code2, Globe, Settings, ChevronDown, ChevronUp,
  Search, Send, Loader2, Activity, Layers, Bot, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

const CATEGORY_COLORS = {
  'Reservaciones': 'blue',
  'CRM': 'emerald',
  'Analytics': 'purple',
  'Comunicación': 'orange',
  'Restaurante': 'rose',
  'IA': 'yellow'
};

const CATEGORY_ICONS = {
  'Reservaciones': <Calendar className="w-4 h-4" />,
  'CRM': <Users className="w-4 h-4" />,
  'Analytics': <BarChart3 className="w-4 h-4" />,
  'Comunicación': <MessageSquare className="w-4 h-4" />,
  'Restaurante': <Layers className="w-4 h-4" />,
  'IA': <Sparkles className="w-4 h-4" />
};

const TESTABLE_TOOLS = [
  { value: 'contexto_restaurante', label: 'contexto_restaurante', needsId: true },
  { value: 'resumen_del_dia', label: 'resumen_del_dia', needsId: true },
  { value: 'estadisticas_dia', label: 'estadisticas_dia', needsId: true },
  { value: 'mejores_clientes', label: 'mejores_clientes', needsId: true },
  { value: 'insights_ia', label: 'insights_ia', needsId: true },
];

export default function AdminMCP() {
  const [status, setStatus] = useState(null);
  const [connInfo, setConnInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTool, setTestTool] = useState('contexto_restaurante');
  const [testRestaurantId, setTestRestaurantId] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [logs, setLogs] = useState([]);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/mcp/status`, { headers: authHeader() });
      if (res.ok) setStatus(await res.json());
    } catch { }
    finally { setLoading(false); }
  }, []);

  const loadConnInfo = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/mcp/connection-info`, { headers: authHeader() });
      if (res.ok) setConnInfo(await res.json());
    } catch { }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/mcp/logs`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch { }
  }, []);

  useEffect(() => {
    loadStatus();
    loadConnInfo();
    loadLogs();
  }, [loadStatus, loadConnInfo, loadLogs]);

  const handleTestTool = async () => {
    if (!testRestaurantId.trim()) {
      toast.error('Ingresa un Restaurant ID para probar');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/mcp/test-tool`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({
          tool_name: testTool,
          params: { restaurant_id: testRestaurantId }
        })
      });
      const data = await res.json();
      setTestResult(data);
    } catch (e) {
      setTestResult({ success: false, error: e.message });
    }
    setTesting(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const groupedTools = (status?.capacidades?.herramientas || []).reduce((acc, tool) => {
    const cat = tool.categoria || 'Otro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(tool);
    return acc;
  }, {});

  const isRunning = status?.servidor?.activo;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-600 to-purple-700 rounded-xl flex items-center justify-center shadow-lg">
                <Bot className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">OkReserva MCP</h1>
                <p className="text-sm text-gray-500">Model Context Protocol — Servidor de herramientas para agentes de IA</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadStatus} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`border-2 ${isRunning ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200'}`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                {isRunning
                  ? <CheckCircle className="w-8 h-8 text-emerald-500" />
                  : <WifiOff className="w-8 h-8 text-gray-400" />}
                <div>
                  <p className="text-xs text-gray-500">Servidor</p>
                  <p className={`font-bold text-sm ${isRunning ? 'text-emerald-700' : 'text-gray-600'}`}>
                    {loading ? 'Verificando...' : isRunning ? 'Activo' : 'Inactivo'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Zap className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-xs text-gray-500">Herramientas</p>
                  <p className="font-bold text-sm text-gray-800">
                    {status?.capacidades?.total_herramientas || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-xs text-gray-500">Recursos</p>
                  <p className="font-bold text-sm text-gray-800">
                    {status?.capacidades?.total_recursos || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-xs text-gray-500">Puerto</p>
                  <p className="font-bold text-sm text-gray-800">
                    {status?.servidor?.puerto || 8001}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="tools">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="tools">Herramientas</TabsTrigger>
            <TabsTrigger value="test">Prueba</TabsTrigger>
            <TabsTrigger value="connect">Conexión</TabsTrigger>
            <TabsTrigger value="config">Config</TabsTrigger>
          </TabsList>

          {/* ─── HERRAMIENTAS ─── */}
          <TabsContent value="tools" className="space-y-3 mt-4">
            <p className="text-sm text-gray-600">
              El servidor MCP expone <strong>{status?.capacidades?.total_herramientas || 0} herramientas</strong> organizadas
              en {Object.keys(groupedTools).length} categorías. Los agentes de IA pueden invocar cualquier herramienta en tiempo real.
            </p>

            {Object.entries(groupedTools).map(([category, tools]) => {
              const color = CATEGORY_COLORS[category] || 'gray';
              const icon = CATEGORY_ICONS[category];
              const isExpanded = expandedCategories[category] !== false;

              return (
                <Card key={category} className="overflow-hidden">
                  <button
                    className="w-full text-left"
                    onClick={() => toggleCategory(category)}
                  >
                    <CardHeader className="py-3 px-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`p-1.5 rounded-lg bg-${color}-100 text-${color}-600`}>
                            {icon}
                          </span>
                          <span className="font-semibold text-gray-800">{category}</span>
                          <Badge variant="secondary" className="text-xs">{tools.length}</Badge>
                        </div>
                        {isExpanded
                          ? <ChevronUp className="w-4 h-4 text-gray-400" />
                          : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </CardHeader>
                  </button>

                  {isExpanded && (
                    <CardContent className="pt-0 pb-3 px-4">
                      <div className="space-y-2">
                        {tools.map(tool => (
                          <div key={tool.nombre}
                            className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                            <code className="text-xs font-mono text-violet-700 bg-violet-50 px-2 py-1 rounded min-w-fit">
                              {tool.nombre}
                            </code>
                            <p className="text-xs text-gray-600 mt-0.5">{tool.descripcion}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}

            {/* Recursos */}
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-indigo-500" />
                  <span className="font-semibold text-gray-800">Recursos (contexto de lectura)</span>
                  <Badge variant="secondary" className="text-xs">
                    {status?.capacidades?.total_recursos || 0}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-3 px-4">
                <div className="space-y-2">
                  {(status?.capacidades?.recursos || []).map(r => (
                    <div key={r.uri}
                      className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50">
                      <code className="text-xs font-mono text-indigo-700 bg-indigo-50 px-2 py-1 rounded min-w-fit">
                        {r.uri}
                      </code>
                      <p className="text-xs text-gray-600 mt-0.5">{r.descripcion}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Prompts */}
            <Card>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-500" />
                  <span className="font-semibold text-gray-800">Prompts de sistema</span>
                  <Badge variant="secondary" className="text-xs">
                    {status?.capacidades?.total_prompts || 0}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-3 px-4">
                <div className="space-y-2">
                  {(status?.capacidades?.prompts || []).map(p => (
                    <div key={p.nombre}
                      className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50">
                      <code className="text-xs font-mono text-yellow-700 bg-yellow-50 px-2 py-1 rounded min-w-fit">
                        {p.nombre}
                      </code>
                      <p className="text-xs text-gray-600 mt-0.5">{p.descripcion}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── PRUEBA ─── */}
          <TabsContent value="test" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Play className="w-4 h-4 text-emerald-600" />
                  Probar herramienta MCP
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Herramienta</Label>
                    <Select value={testTool} onValueChange={setTestTool}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TESTABLE_TOOLS.map(t => (
                          <SelectItem key={t.value} value={t.value} className="text-sm">
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Restaurant ID</Label>
                    <Input
                      className="h-9 text-sm font-mono"
                      placeholder="ej: rest_abc123"
                      value={testRestaurantId}
                      onChange={e => setTestRestaurantId(e.target.value)}
                    />
                  </div>
                </div>

                <Button
                  onClick={handleTestTool}
                  disabled={testing}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {testing
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ejecutando...</>
                    : <><Play className="w-4 h-4 mr-2" /> Ejecutar herramienta</>}
                </Button>

                {testResult && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {testResult.success
                          ? <CheckCircle className="w-4 h-4 text-emerald-500" />
                          : <XCircle className="w-4 h-4 text-red-500" />}
                        <span className="text-sm font-medium">
                          {testResult.success ? 'Herramienta ejecutada correctamente' : 'Error al ejecutar'}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(JSON.stringify(testResult.result || testResult, null, 2))}
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copiar
                      </Button>
                    </div>
                    <pre className="bg-gray-900 text-green-400 text-xs p-4 rounded-lg overflow-auto max-h-96 font-mono">
                      {JSON.stringify(testResult.result || testResult, null, 2)}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Logs */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Terminal className="w-4 h-4 text-gray-600" />
                    Logs del servidor MCP
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={loadLogs}>
                    <RefreshCw className="w-3 h-3 mr-1" /> Refrescar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-900 rounded-lg p-3 h-48 overflow-auto font-mono text-xs">
                  {logs.length > 0
                    ? logs.map((line, i) => (
                      <div key={i} className={`${line.includes('ERROR') ? 'text-red-400' : line.includes('WARNING') ? 'text-yellow-400' : line.includes('INFO') ? 'text-blue-400' : 'text-gray-400'}`}>
                        {line}
                      </div>
                    ))
                    : <p className="text-gray-500">
                      {isRunning ? 'No hay logs recientes.' : '⚡ Inicia el servidor MCP para ver logs aquí.'}
                    </p>
                  }
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── CONEXIÓN ─── */}
          <TabsContent value="connect" className="space-y-4 mt-4">

            {/* Endpoint SSE */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Wifi className="w-4 h-4 text-blue-600" />
                  Endpoint de conexión (SSE)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-600">
                  Conecta cualquier cliente MCP compatible usando Server-Sent Events.
                  Funciona con Claude Desktop, agentes custom y OpenRouter.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 text-gray-800 px-3 py-2 rounded text-sm font-mono">
                    {status?.servidor?.url_sse || `http://localhost:8001/sse`}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(status?.servidor?.url_sse || 'http://localhost:8001/sse')}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Claude Desktop */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-purple-600" />
                  Claude Desktop (stdio)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-600">
                  Agrega esta configuración a tu archivo <code className="text-xs bg-gray-100 px-1 rounded">claude_desktop_config.json</code>:
                </p>
                <div className="relative">
                  <pre className="bg-gray-900 text-green-400 text-xs p-4 rounded-lg overflow-auto font-mono max-h-64">
                    {connInfo ? JSON.stringify(connInfo.claude_desktop, null, 2) : 'Cargando...'}
                  </pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-2 right-2 text-gray-400 hover:text-white"
                    onClick={() => copyToClipboard(JSON.stringify(connInfo?.claude_desktop, null, 2))}
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* OpenRouter */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="w-4 h-4 text-orange-600" />
                  OpenRouter + MCP
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-600">
                  Modelos compatibles a través de OpenRouter con soporte MCP nativo:
                </p>
                <div className="flex flex-wrap gap-2">
                  {(status?.integracion_openrouter?.compatible_con || []).map(model => (
                    <Badge key={model} variant="secondary" className="text-xs font-mono">
                      {model}
                    </Badge>
                  ))}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-700">
                    <strong>Modelo recomendado:</strong>{' '}
                    <code className="font-mono">{status?.integracion_openrouter?.modelo_recomendado || 'anthropic/claude-3-5-sonnet'}</code>
                    {' '} — Mejor comprensión de herramientas y contexto de restaurantes.
                  </p>
                </div>

                {/* Ejemplo código */}
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 font-medium">Ejemplo de uso con Python:</p>
                  <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-auto font-mono">
{`import anthropic

client = anthropic.Anthropic(
    base_url="https://openrouter.ai/api/v1",
    api_key="YOUR_OPENROUTER_KEY"
)

# El agente tiene acceso a todas las herramientas de OkReserva
response = client.beta.messages.create(
    model="anthropic/claude-3-5-sonnet",
    max_tokens=1024,
    mcp_servers=[{
        "type": "url",
        "url": "${status?.servidor?.url_sse || 'http://localhost:8001/sse'}",
        "name": "okreserva"
    }],
    messages=[{
        "role": "user",
        "content": "¿Cuántas reservaciones hay hoy en resto_abc123?"
    }]
)`}
                  </pre>
                </div>
              </CardContent>
            </Card>

            {/* Agente propio */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Code2 className="w-4 h-4 text-gray-600" />
                  Agente personalizado (API directa)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-600">
                  Llama directamente a las herramientas MCP desde tu propio código:
                </p>
                <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-auto font-mono max-h-48">
{`import httpx

async def call_mcp_tool(tool_name, params):
    async with httpx.AsyncClient() as client:
        # Listar herramientas disponibles
        tools = await client.post(
            "${status?.servidor?.url_sse || 'http://localhost:8001'}/tools/list"
        )
        
        # Llamar herramienta específica
        result = await client.post(
            "${status?.servidor?.url_sse || 'http://localhost:8001'}/tools/call",
            json={"name": tool_name, "arguments": params}
        )
        return result.json()

# Ejemplo
result = await call_mcp_tool(
    "buscar_reservaciones",
    {"restaurant_id": "rest_abc123", "fecha": "2025-04-15"}
)`}
                </pre>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── CONFIG ─── */}
          <TabsContent value="config" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="w-4 h-4 text-gray-600" />
                  Estado de configuración
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  {
                    label: 'SendPulse (WhatsApp)',
                    configured: status?.configuracion?.sendpulse_configurado,
                    detail: 'SENDPULSE_API_USER_ID + SENDPULSE_API_SECRET',
                    required: 'Requerido para herramientas de comunicación'
                  },
                  {
                    label: 'OpenRouter (LLM)',
                    configured: status?.configuracion?.openrouter_configurado,
                    detail: 'OPENROUTER_API_KEY',
                    required: 'Requerido para agentes de IA integrados'
                  },
                  {
                    label: 'MCP API Key',
                    configured: status?.configuracion?.api_key_configurada,
                    detail: 'MCP_API_KEY',
                    required: 'Seguridad del servidor MCP'
                  }
                ].map(item => (
                  <div key={item.label}
                    className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.label}</p>
                      <p className="text-xs text-gray-500 font-mono">{item.detail}</p>
                      <p className="text-xs text-gray-400">{item.required}</p>
                    </div>
                    {item.configured
                      ? <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                        <CheckCircle className="w-3 h-3" /> Configurado
                      </Badge>
                      : <Badge className="bg-red-100 text-red-700 gap-1">
                        <XCircle className="w-3 h-3" /> Falta
                      </Badge>}
                  </div>
                ))}

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                  <p className="text-xs text-amber-800">
                    <strong>Restaurante por defecto:</strong>{' '}
                    <code className="font-mono">{status?.configuracion?.restaurant_default || 'no configurado'}</code>
                    <br />
                    Configura <code className="font-mono">MCP_DEFAULT_RESTAURANT</code> como variable de entorno
                    para que el servidor MCP use ese restaurante cuando el agente no especifique uno.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Cómo iniciar el servidor */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-gray-600" />
                  Cómo iniciar el servidor MCP
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-600">
                  El servidor MCP corre independientemente en el puerto <strong>8001</strong>.
                  Está configurado como workflow <code className="text-xs bg-gray-100 px-1 rounded">MCP Server</code>.
                </p>

                <div className="space-y-2">
                  <p className="text-xs text-gray-500 font-medium">Modo SSE (web, default):</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-900 text-green-400 px-3 py-2 rounded text-xs font-mono">
                      cd backend && python -m mcp_server.main sse
                    </code>
                    <Button size="sm" variant="ghost"
                      onClick={() => copyToClipboard('cd backend && python -m mcp_server.main sse')}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-gray-500 font-medium">Modo stdio (Claude Desktop):</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-900 text-green-400 px-3 py-2 rounded text-xs font-mono">
                      cd backend && python -m mcp_server.main stdio
                    </code>
                    <Button size="sm" variant="ghost"
                      onClick={() => copyToClipboard('cd backend && python -m mcp_server.main stdio')}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
